import clsx from "clsx";
import { type RefObject, useEffect, useState } from "react";

/** Bords d'une zone qui défile : contenu caché au-dessus (`top`) ou au-dessous (`bottom`). Suivi au défilement et aux
 *  changements de taille de la zone et de ses enfants ; `layout` : valeur qui, en changeant, refait la mesure
 *  (barre repliée ou dépliée…). */
export function useScrollEdges(ref: RefObject<HTMLElement | null>, layout?: unknown) {
  const [edges, setEdges] = useState({ top: false, bottom: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const top = el.scrollTop > 2;
      const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 2;
      setEdges((e) => (e.top === top && e.bottom === bottom ? e : { top, bottom }));
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(el);
    for (const child of Array.from(el.children)) observer?.observe(child);
    return () => {
      el.removeEventListener("scroll", update);
      observer?.disconnect();
    };
  }, [ref, layout]);
  return edges;
}

/** Fondus des bords d'une zone qui défile (barre latérale, panneaux) : du blanc de la surface vers le transparent,
 *  en haut (20 px) et en bas (36 px), quand du contenu y est caché ; ils apparaissent et disparaissent en douceur.
 *  Rien ne vient alors buter contre un filet gris (pied collant, bord du panneau). À poser dans un parent
 *  `relative` de la zone qui défile. */
export function ScrollFades({ edges }: { edges: { top: boolean; bottom: boolean } }) {
  return (
    <>
      <div
        aria-hidden
        className={clsx(
          "pointer-events-none absolute inset-x-0 top-0 h-5 bg-linear-to-b from-surface to-transparent transition-opacity duration-(--gx-enter)",
          edges.top ? "opacity-100" : "opacity-0",
        )}
      />
      <div
        aria-hidden
        className={clsx(
          "pointer-events-none absolute inset-x-0 bottom-0 h-9 bg-linear-to-t from-surface to-transparent transition-opacity duration-(--gx-enter)",
          edges.bottom ? "opacity-100" : "opacity-0",
        )}
      />
    </>
  );
}
